/**
 * The update request a Save sends (a nested table row's, an input box's). Everything
 * is fixed except `requestData` - the business keys with their values, plus the
 * saved columns - and the `entity`.
 */
import type { DesignNode } from '../design-builder.types';

/** Widgets whose current value a "Save changes" button can send as a column. */
export const VALUE_SOURCE_LAYOUT_TYPES = ['INPUT_BOX', 'DROPDOWN', 'SLIDER', 'ZONE_SLIDER'] as const;

const VALUE_SOURCE_KIND: Record<string, string> = { INPUT_BOX: 'Input box', DROPDOWN: 'Dropdown', SLIDER: 'Slider', ZONE_SLIDER: 'Zone slider' };

/** The value widgets and data-bound texts among `nodes` (any depth), as "Value from" options (by node id). */
export function valueSourceOptionsOf(nodes: ReadonlyArray<DesignNode>): Array<{ label: string; value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  const visit = (list: ReadonlyArray<DesignNode>) => list.forEach((node) => {
    const layoutType = node.widget?.layoutType ?? '';
    if (node.type === 'WIDGET' && (VALUE_SOURCE_LAYOUT_TYPES as readonly string[]).includes(layoutType)) {
      out.push({ label: `${node.name || node.id} (${VALUE_SOURCE_KIND[layoutType]})`, value: node.id });
    } else if (node.type === 'TEXT' && node.dataBinding?.field) {
      out.push({ label: `${node.name || node.id} (Text: ${node.dataBinding.field})`, value: node.id });
    }
    if (node.children?.length) visit(node.children);
  });
  visit(nodes);
  return out;
}

/** An input box's "Save button (update)" (Data tab), stored on `widget.binding.save`. */
export interface InputSaveConfig {
  enabled?: boolean;
  label?: string;
  url?: string;
  /** Entity the update targets (default: the "Fill from data" dataset). */
  entity?: string;
  /** Columns identifying the record; their values come from the current filters. */
  businessKeys?: string[];
  /** The column the box's value is saved into (default: the "Fill from data" column). */
  column?: string;
}

export function inputSaveConfigOf(binding: Record<string, unknown> | undefined | null): InputSaveConfig | null {
  const raw = binding?.['save'];
  if (!raw || typeof raw !== 'object') return null;
  const save = raw as InputSaveConfig;
  return save.enabled === true ? save : null;
}

export function updateRequestBody(
  entity: string,
  businessKeys: string[],
  keyValue: (key: string) => unknown,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const keys = businessKeys.filter(Boolean);
  const requestData: Record<string, unknown> = { operation: 'update', businessKey: keys };
  keys.forEach((key) => (requestData[key] = keyValue(key) ?? null));
  Object.assign(requestData, values);
  return {
    requestData,
    responseList: [],
    entity,
    workflowId: 'UI',
    endpoints: [],
    communicationType: 'rest',
    version: '1.0',
  };
}

/** A typed value as sent: a number when it reads as one, null when empty, else the text. */
export function typedSaveValue(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return value;
  const text = value.trim();
  if (!text) return null;
  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : text;
}
